import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ScatterChart,
  Scatter,
  AreaChart,
  Area,
  Cell
} from 'recharts';
import { ArrowTrendingDownIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface RiskMetrics {
  portfolioVaR95: number;
  portfolioVaR99: number;
  expectedShortfall: number;
  beta: number;
  correlation: Record<string, number>;
  concentrationRisk: {
    maxAssetWeight: number;
    herfindahlIndex: number;
    diversificationRatio: number;
  };
}

interface PerformanceData {
  period: number;
  totalReturn: number;
  annualizedReturn: number;
  volatility: number;
  sharpeRatio: number;
  maxDrawdown: number;
}

interface RiskVisualizationSuiteProps {
  riskData?: RiskMetrics;
  performanceData?: PerformanceData;
  period: number;
}

const RiskVisualizationSuite: React.FC<RiskVisualizationSuiteProps> = ({ 
  riskData, 
  performanceData, 
  period 
}) => {
  if (!riskData && !performanceData) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading risk visualizations...</p>
        </div>
      </div>
    );
  }

  // Generate VaR timeline data (simulated based on current metrics)
  const generateVaRTimeline = () => {
    const data = [];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - period);

    for (let i = 0; i <= period; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + i);
      
      // Simulate VaR variations with some randomness
      const baseVaR95 = riskData?.portfolioVaR95 || -3.5;
      const baseVaR99 = riskData?.portfolioVaR99 || -5.2;
      const variation = (Math.random() - 0.5) * 0.5; // Small random variation
      
      data.push({
        date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: currentDate.toISOString(),
        VaR95: Number((baseVaR95 + variation).toFixed(2)),
        VaR99: Number((baseVaR99 + variation * 1.5).toFixed(2)),
        ExpectedShortfall: Number(((baseVaR99 + variation * 1.5) * 1.3).toFixed(2))
      });
    }
    return data;
  };

  // Generate risk-return scatter data for different assets/strategies
  const generateRiskReturnData = () => {
    const assets = ['Portfolio', 'BTC', 'ETH', 'SPY', 'Conservative', 'Aggressive'];
    const colors = ['#3b82f6', '#f7931a', '#627eea', '#4ade80', '#8b5cf6', '#ef4444'];
    
    return assets.map((asset, index) => {
      let baseReturn, baseRisk;
      
      // Set realistic return/risk for each asset type
      switch (asset) {
        case 'Portfolio':
          baseReturn = performanceData?.annualizedReturn || 12;
          baseRisk = performanceData?.volatility || 15;
          break;
        case 'BTC':
          baseReturn = 45;
          baseRisk = 85;
          break;
        case 'ETH':
          baseReturn = 35;
          baseRisk = 75;
          break;
        case 'SPY':
          baseReturn = 10;
          baseRisk = 12;
          break;
        case 'Conservative':
          baseReturn = 6;
          baseRisk = 8;
          break;
        case 'Aggressive':
          baseReturn = 20;
          baseRisk = 30;
          break;
        default:
          baseReturn = 10;
          baseRisk = 15;
      }

      return {
        name: asset,
        return: baseReturn,
        risk: baseRisk,
        sharpe: Number((baseReturn / baseRisk).toFixed(2)),
        color: colors[index],
        size: asset === 'Portfolio' ? 120 : 80 // Make portfolio point larger
      };
    });
  };

  // Generate drawdown timeline
  const generateDrawdownData = () => {
    const data = [];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - period);
    
    let currentDrawdown = 0;
    let peak = 100;
    
    for (let i = 0; i <= period; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + i);
      
      // Simulate portfolio value changes
      const dailyChange = (Math.random() - 0.48) * 2; // Slight upward bias
      const newValue = peak + dailyChange;
      
      if (newValue > peak) {
        peak = newValue;
        currentDrawdown = 0;
      } else {
        currentDrawdown = ((newValue - peak) / peak) * 100;
      }
      
      data.push({
        date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        drawdown: Number(currentDrawdown.toFixed(2)),
        portfolioValue: Number(newValue.toFixed(2))
      });
    }
    return data;
  };

  const varData = generateVaRTimeline();
  const riskReturnData = generateRiskReturnData();
  const drawdownData = generateDrawdownData();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey}: {entry.value}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const ScatterTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{data.name}</p>
          <p className="text-sm text-gray-600">Return: {data.return}%</p>
          <p className="text-sm text-gray-600">Risk: {data.risk}%</p>
          <p className="text-sm text-gray-600">Sharpe: {data.sharpe}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8">
      {/* Risk Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mr-3" />
            <div>
              <div className="text-2xl font-bold text-red-700">
                {Math.abs(riskData?.portfolioVaR95 || 0).toFixed(1)}%
              </div>
              <div className="text-sm text-red-600">Value at Risk (95%)</div>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <ArrowTrendingDownIcon className="h-8 w-8 text-yellow-500 mr-3" />
            <div>
              <div className="text-2xl font-bold text-yellow-700">
                {Math.abs(performanceData?.maxDrawdown || 0).toFixed(1)}%
              </div>
              <div className="text-sm text-yellow-600">Maximum Drawdown</div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-blue-500 mr-3" />
            <div>
              <div className="text-2xl font-bold text-blue-700">
                {(performanceData?.sharpeRatio || 0).toFixed(2)}
              </div>
              <div className="text-sm text-blue-600">Sharpe Ratio</div>
            </div>
          </div>
        </div>
      </div>

      {/* VaR Timeline Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Value at Risk Timeline</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={varData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                stroke="#666" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="#666" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="VaR95" 
                stroke="#ef4444" 
                strokeWidth={2}
                dot={false}
                name="VaR 95%"
              />
              <Line 
                type="monotone" 
                dataKey="VaR99" 
                stroke="#dc2626" 
                strokeWidth={2}
                dot={false}
                name="VaR 99%"
              />
              <Line 
                type="monotone" 
                dataKey="ExpectedShortfall" 
                stroke="#991b1b" 
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Expected Shortfall"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Risk-Return Scatter Plot */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Risk-Return Analysis</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart data={riskReturnData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                type="number"
                dataKey="risk"
                stroke="#666"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}%`}
                name="Risk"
              />
              <YAxis 
                type="number"
                dataKey="return"
                stroke="#666"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}%`}
                name="Return"
              />
              <Tooltip content={<ScatterTooltip />} />
              <Scatter name="Assets" dataKey="return" fill="#3b82f6">
                {riskReturnData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p>Each point represents an asset's risk-return profile. Higher and further left is better.</p>
        </div>
      </div>

      {/* Drawdown Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Portfolio Drawdown Analysis</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={drawdownData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                stroke="#666" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="#666" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="drawdown" 
                stroke="#ef4444" 
                fill="#fef2f2"
                strokeWidth={2}
                name="Drawdown"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p>Drawdown shows peak-to-trough decline in portfolio value. Lower is better.</p>
        </div>
      </div>
    </div>
  );
};

export default RiskVisualizationSuite; 